import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronLeft, Plus } from 'lucide-react';
import {
  CUSTOM_FIELD_MODULES,
  CUSTOM_FIELD_MODULE_DEFINITIONS,
  type CustomFieldModule,
  type ResolvedCustomField,
} from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { CustomFieldFormDialog } from '../components/custom-fields/custom-field-form-dialog.js';
import { CustomFieldList } from '../components/custom-fields/custom-field-list.js';
import { Button, useConfirm, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { fieldInUseCount } from '../lib/custom-fields/custom-fields-api.js';
import {
  useCustomFields,
  useDeleteCustomField,
  useReorderCustomFields,
  useResolvedCustomFields,
  useUpdateCustomField,
} from '../lib/custom-fields/use-custom-fields.js';

/**
 * One board for every module's fields, laid out like a desk: a column per module, side by side.
 *
 * Columns rather than tabs, because the person doing this is setting the shop up and moves back
 * and forth between modules. The row scrolls sideways and each column scrolls on its own, so one
 * long form never pushes another off the screen.
 *
 * Each module keeps its own list, its own cap and its own column count; what never varies is the
 * mechanics — add, rename, reorder, hide, and the 409 that offers hiding when deleting would
 * discard what somebody wrote. The old `settings/leads` address redirects here.
 */

/** Past this the sheet is unreadable on anything smaller than a desk. */
const COMFORTABLE_COLUMNS = 10;

export function CustomFieldsPage(): ReactElement {
  const { t } = useTranslation();

  return (
    <>
      <PageHeader title={t('customFields.title')} description={t('customFields.description')} />

      <div className="flex flex-col gap-5">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('customFields.back')}
        </Link>

        <div className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:scroll-px-0 sm:px-0">
          {CUSTOM_FIELD_MODULES.map((module) => (
            <ModuleFieldEditor key={module} module={module} />
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * One module's column: its fields, its actions, and what it has used of its cap. Each column
 * owns its dialogs, so an edit never carries across from one module to the other.
 */
function ModuleFieldEditor({ module }: { module: CustomFieldModule }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();
  const ns = 'customFields';
  const cap = CUSTOM_FIELD_MODULE_DEFINITIONS[module].maxCustomFields;

  const query = useCustomFields(module);
  const { fields, labelOf } = useResolvedCustomFields(module);
  const reorder = useReorderCustomFields(module);
  const update = useUpdateCustomField(module);
  const remove = useDeleteCustomField(module);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ResolvedCustomField | null>(null);

  // The API already sends the product's fields first; the split here is only which heading each
  // falls under. Reordering one half puts the other back around it, so the request still names
  // every key.
  const builtins = useMemo(() => fields.filter((field) => field.origin === 'builtin'), [fields]);
  const customs = useMemo(() => fields.filter((field) => field.origin === 'custom'), [fields]);
  const customCount = customs.length;
  const builtinKeys = builtins.map((field) => field.key);
  const customKeys = customs.map((field) => field.key);
  const columnCount = useMemo(
    () => fields.filter((field) => !field.hidden && field.showInTable).length,
    [fields],
  );

  /**
   * Deleting is guarded by usage on the server. A 409 here is an answer rather than a failure —
   * the field is filled in somewhere — so it turns into the offer to hide it instead, which is
   * what the shop actually wanted and the only option that keeps what was written.
   */
  const onDelete = async (field: ResolvedCustomField): Promise<void> => {
    const label = labelOf(field);
    const confirmed = await confirm({
      title: t(`${ns}.deleteTitle`, { name: label }),
      description: t(`${ns}.deleteDescription`),
      confirmLabel: t('common.delete'),
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await remove.mutateAsync(field.key);
      toast({ title: t(`${ns}.deleted`, { name: label }), variant: 'success' });
    } catch (error) {
      const usageCount = fieldInUseCount(error);
      if (usageCount === null) {
        toast({ title: t('error.unexpected'), variant: 'destructive' });
        return;
      }

      const hideInstead = await confirm({
        title: t(`${ns}.inUseTitle`),
        description: t(`${ns}.inUse`, { count: usageCount }),
        confirmLabel: t(`${ns}.hideInstead`),
      });
      if (!hideInstead) return;
      await update.mutateAsync({ fieldKey: field.key, input: { hidden: true } });
      toast({ title: t(`${ns}.saved`), variant: 'success' });
    }
  };

  const onToggleHidden = async (field: ResolvedCustomField): Promise<void> => {
    try {
      await update.mutateAsync({ fieldKey: field.key, input: { hidden: !field.hidden } });
      toast({ title: t(`${ns}.saved`), variant: 'success' });
    } catch {
      toast({ title: t('error.unexpected'), variant: 'destructive' });
    }
  };

  const busy = reorder.isPending || update.isPending || remove.isPending;
  const full = customCount >= cap;
  const title = t(`customFields.module.${module}`);

  return (
    <section
      aria-label={title}
      className="flex h-[calc(100dvh-20rem)] min-h-[22rem] sm:h-[calc(100dvh-15rem)] w-[340px] max-w-[85vw] shrink-0 snap-start flex-col rounded-lg border border-border bg-card"
    >
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          <span aria-hidden="true" className="text-destructive">
            *
          </span>{' '}
          {t('customFields.legend')}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {query.isPending ? (
          <ListSkeleton rows={6} />
        ) : query.isError ? (
          // The defaults would resolve without the request, but a list somebody cannot save is
          // worse than none: a refusal shows as a refusal.
          <p role="alert" className="p-1 text-sm text-muted-foreground">
            {isApiRequestError(query.error) && query.error.status === 403
              ? t('customFields.forbidden')
              : t('error.unexpected')}
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            <section aria-labelledby={`${module}-default-fields`} className="flex flex-col gap-2">
              <h4
                id={`${module}-default-fields`}
                className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                {t('customFields.defaultSection')}
              </h4>
              <CustomFieldList
                fields={builtins}
                labelOf={labelOf}
                onEdit={setEditing}
                onDelete={(field) => void onDelete(field)}
                onToggleHidden={(field) => void onToggleHidden(field)}
                onReorder={(order) => reorder.mutate({ order: [...order, ...customKeys] })}
                disabled={busy}
              />
            </section>

            <section
              aria-labelledby={`${module}-additional-fields`}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center justify-between gap-3 px-1">
                <h4
                  id={`${module}-additional-fields`}
                  className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                >
                  {t('customFields.additionalSection')}
                </h4>
                <div className="flex items-center gap-3 text-sm">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span aria-hidden="true" className="size-1.5 rounded-full bg-primary" />
                    {t('customFields.usage', { count: customCount, max: cap })}
                  </span>
                  <Button
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => setAdding(true)}
                    disabled={full}
                  >
                    <Plus aria-hidden="true" className="size-4" />
                    {t(`${ns}.add`)}
                  </Button>
                </div>
              </div>
              {customs.length > 0 ? (
                <CustomFieldList
                  fields={customs}
                  labelOf={labelOf}
                  onEdit={setEditing}
                  onDelete={(field) => void onDelete(field)}
                  onToggleHidden={(field) => void onToggleHidden(field)}
                  onReorder={(order) => reorder.mutate({ order: [...builtinKeys, ...order] })}
                  disabled={busy}
                />
              ) : (
                <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
                  {t('customFields.additionalEmpty')}
                </p>
              )}
            </section>
          </div>
        )}
      </div>

      {query.isSuccess ? (
        <div className="flex flex-col gap-1 border-t border-border px-4 py-3 text-sm">
          <p className="text-muted-foreground">{t(`${ns}.columnCount`, { count: columnCount })}</p>
          {columnCount > COMFORTABLE_COLUMNS ? (
            <p className="text-destructive">{t(`${ns}.tooManyColumns`)}</p>
          ) : null}
          {full ? (
            <p className="text-muted-foreground">{t(`${ns}.limitReached`, { count: cap })}</p>
          ) : null}
        </div>
      ) : null}

      <CustomFieldFormDialog module={module} open={adding} onOpenChange={setAdding} />

      {editing ? (
        <CustomFieldFormDialog
          module={module}
          open
          onOpenChange={(open) => !open && setEditing(null)}
          field={editing}
          currentLabel={labelOf(editing)}
          onSaved={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}
